import React, { useState, useRef } from 'react';

interface MobileMetricSliderProps {
  cards: React.ReactNode[];
  height?: string;
}

const MobileMetricSlider: React.FC<MobileMetricSliderProps> = ({ cards, height = '200px' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const minSwipeDistance = 40;

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const next = () => {
    if (currentIndex < cards.length - 1) {
      goToSlide(currentIndex + 1);
    }
  };

  const prev = () => {
    if (currentIndex > 0) {
      goToSlide(currentIndex - 1);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > minSwipeDistance) next();
    if (distance < -minSwipeDistance) prev();
  };

  return (
    <div style={{ 
      width: '100%', 
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Slides container */}
      <div
        style={{
          position: 'relative',
          height,
          overflow: 'visible',
          width: '95%'
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {cards.map((card, index) => {
          const active = index === currentIndex;
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: active ? 1 : 0,
                transform: `translateX(${active ? '0' : (index > currentIndex ? '100%' : '-100%')}) scale(${active ? 1.0 : 0.98})`,
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                pointerEvents: active ? 'auto' : 'none',
              }}
            >
              {card}
            </div>
          );
        })}
      </div>
      
      {/* Pip indicators */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '1rem',
        gap: '0.5rem'
      }}>
        {cards.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isTransitioning}
            style={{
              width: '8px',
              height: '8px',
              minWidth: '8px',
              minHeight: '8px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: index === currentIndex ? 'rgb(34, 34, 69)' : '#ccc',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              padding: 0
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default MobileMetricSlider;
